import { useEffect, useRef, useState } from 'react';

interface LightboxImage {
  src: string;
  alt: string;
  portrait?: boolean;
}

interface LightboxTransform {
  scale: number;
  x: number;
  y: number;
}

const RESET: LightboxTransform = { scale: 1, x: 0, y: 0 };
const MIN_SCALE = 0.25;
const MAX_SCALE = 8;

function fullImageSource(img: HTMLImageElement): string {
  // Pasted inline images follow the composer convention: a preview <img>
  // wrapped in an <a> whose href is the original file.
  const link = img.closest('a');
  if (link?.href && (link.childNodes.length === 1 || /\.(png|jpe?g|gif|webp|bmp|avif)(\?|$)/i.test(link.href))) {
    return link.href;
  }
  return img.currentSrc || img.src;
}

/**
 * Telegram-style viewer for images inside rendered rich content: click an
 * image to open it full-size in an overlay, zoom with the mouse wheel
 * (Ctrl+wheel included), drag to pan when zoomed, click again or press
 * Escape to close. Mounted once in the app shell.
 */
export function ImageLightbox() {
  const [image, setImage] = useState<LightboxImage | null>(null);
  const [transform, setTransform] = useState<LightboxTransform>(RESET);
  const overlayRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ pointerId: number; lastX: number; lastY: number; moved: boolean } | null>(null);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const clicked = event.target;
      if (!(clicked instanceof Element)) return;
      const portraitLink = clicked.closest('[data-lightbox-image]');
      // These surfaces provide their own viewer with annotation actions.
      // Keep the existing lightbox for contact photos and all other pages.
      if (!portraitLink && clicked.closest('.telegram-chat .message-list, .telegram-task-card .state-history, .rich-editor--messenger, .chat-media-viewer, .annotation-modal')) return;
      const target = portraitLink?.querySelector('img') ?? clicked;
      if (!(target instanceof HTMLImageElement) || (!portraitLink && !target.closest('.rich-content'))) return;
      // Ctrl/Cmd/middle click keeps the browser's open-in-tab behavior.
      if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey) return;
      event.preventDefault();
      event.stopPropagation();
      setTransform(RESET);
      setImage({ src: fullImageSource(target), alt: target.alt, portrait: Boolean(portraitLink) });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  useEffect(() => {
    if (!image) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setImage(null);
      }
    };
    // Capture phase so an open modal underneath does not also close.
    document.addEventListener('keydown', onKeyDown, true);
    document.body.classList.add('modal-open');
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (!document.querySelector('.modal-backdrop')) document.body.classList.remove('modal-open');
    };
  }, [image]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay || !image) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      setTransform((current) => {
        const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, current.scale * Math.exp(-event.deltaY * 0.0018)));
        if (scale === current.scale) return current;
        // Keep the point under the cursor fixed while zooming.
        const bounds = overlay.getBoundingClientRect();
        const cursorX = event.clientX - bounds.left - bounds.width / 2;
        const cursorY = event.clientY - bounds.top - bounds.height / 2;
        const ratio = scale / current.scale;
        return {
          scale,
          x: cursorX - (cursorX - current.x) * ratio,
          y: cursorY - (cursorY - current.y) * ratio,
        };
      });
    };
    overlay.addEventListener('wheel', onWheel, { passive: false });
    return () => overlay.removeEventListener('wheel', onWheel);
  }, [image]);

  if (!image) return null;

  const beginDrag = (event: React.PointerEvent<HTMLImageElement>) => {
    if (event.button !== 0) return;
    dragRef.current = { pointerId: event.pointerId, lastX: event.clientX, lastY: event.clientY, moved: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const drag = (event: React.PointerEvent<HTMLImageElement>) => {
    const state = dragRef.current;
    if (!state || state.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - state.lastX;
    const deltaY = event.clientY - state.lastY;
    if (!state.moved && Math.hypot(deltaX, deltaY) < 4) return;
    state.moved = true;
    state.lastX = event.clientX;
    state.lastY = event.clientY;
    setTransform((current) => ({ ...current, x: current.x + deltaX, y: current.y + deltaY }));
  };
  const endDrag = (event: React.PointerEvent<HTMLImageElement>) => {
    const state = dragRef.current;
    if (!state || state.pointerId !== event.pointerId) return;
    dragRef.current = null;
    // A plain click (no drag) closes the viewer, like Telegram.
    if (!state.moved) setImage(null);
  };

  return (
    <div
      aria-label={image.alt || 'Просмотр изображения'}
      className={`image-lightbox${image.portrait ? ' image-lightbox--portrait' : ''}`}
      onClick={(event) => {
        if (event.target === event.currentTarget) setImage(null);
      }}
      ref={overlayRef}
      role="dialog"
    >
      <img
        alt={image.alt}
        className={transform.scale > 1 ? 'is-zoomed' : ''}
        draggable={false}
        onPointerCancel={() => { dragRef.current = null; }}
        onPointerDown={beginDrag}
        onPointerMove={drag}
        onPointerUp={endDrag}
        src={image.src}
        style={{ transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})` }}
      />
      <span className="image-lightbox__hint">
        {transform.scale !== 1 ? `${Math.round(transform.scale * 100)}% · ` : ''}
        колесо — масштаб, клик — закрыть
      </span>
    </div>
  );
}
